import { ICoreContext } from '@msdyn365-commerce/core';
import React, { useEffect, useState } from 'react';
import { IOrderDetailLine, IOrderDetailResponse } from '../../../actions/order-details/get-order-details.action';
import DataTitle from '../../../components/data-title';
import { useModal } from '../../../components/modal';
import { NO_DATA_FALLBACK } from '../../../configuration';
import { DELIVERY_INFORMATION_LEG_STATES } from '../../../configuration/delivery-information';
import { ILocalizationState } from '../../../states/localization-state';
import {
    checkIfValidDate,
    checkIfValidNumber,
    dateLocaleFormatter,
    formatCurrency,
    formatNumberLocaleStatic,
    formatOverallStatusCodeForTooltip,
    formatStatusCodeForTooltip,
    mapItemInvoiceStatusCodeToText,
    mapItemStatusCodeDescriptionToText,
    mapItemStatusCodeToText,
    toLocalDate
} from '../../../utilities';
import { IOrderDetailsViewProps } from '../order-details';
import { IOrderDetailsResources } from '../order-details.props.autogenerated';
import DeliveryInformationModal from './delivery-information-modal';
import { ExpandedRows, getFirstLine } from './expanded-order-details-rows';
import { getReorderPageUrl } from './get-reorder-url';

interface LatestItemValues {
    status?: string;
    quantity?: string;
    date?: string;
    unit?: string;
    info?: string;
}

export default function orderItems({
    orderDetails,
    resources,
    context,
    localizationState
}: {
    context: ICoreContext;
    orderDetails: IOrderDetailResponse;
    resources: IOrderDetailsResources;
    props: IOrderDetailsViewProps;
    localizationState: ILocalizationState;
}) {
    const [modalIndex, setModalIndex] = useState<number | null>(null);
    const [deliveryItems, setDeliveryItems] = useState<{ line: IOrderDetailLine; index: number }[]>([]);
    const [expandedRowIndex, setExpandedRowIndex] = useState<number | null>(null);

    const { setOpen, isOpen } = useModal();
    const goToNext = () => {
        let nextStep;
        if (modalIndex === null || modalIndex === deliveryItems.length - 1) {
            nextStep = 0;
        } else {
            nextStep = modalIndex + 1;
        }
        setModalIndex(nextStep);
    };

    const goToPrev = () => {
        let prevStep;
        if (modalIndex === null || modalIndex === 0) {
            prevStep = deliveryItems.length - 1;
        } else {
            prevStep = modalIndex - 1;
        }
        setModalIndex(prevStep);
    };

    const getPreferredInfo = (item: IOrderDetailLine, resources: IOrderDetailsResources): LatestItemValues => {
        const firstLine = getFirstLine(item);
        const status = mapItemStatusCodeToText(item.StateLogistics, resources);

        switch (status) {
            case resources.globalStatusItemRequested:
                return { status, date: item.RequestedDate, quantity: item.RequestedQty, unit: item.RequestedUnit };
            case resources.globalStatusItemConfirmed:
                return { status, date: firstLine?.ConfirmedDate, quantity: firstLine?.ConfirmedQty, unit: firstLine?.ConfirmedUnit };
            case resources.globalStatusItemTransportPlanned:
                return {
                    status,
                    date: firstLine?.ConfirmedDate ? firstLine.ConfirmedDate : item.RequestedDate,
                    quantity: firstLine?.ConfirmedQty ? firstLine.ConfirmedQty : item.RequestedQty,
                    unit: firstLine?.ConfirmedUnit ? firstLine.ConfirmedUnit : item.RequestedUnit
                };
            case resources.globalStatusItemGoodsDispatched:
                return { status, date: item.GoodsMovementDate, quantity: firstLine?.ConfirmedQty, unit: firstLine?.ConfirmedUnit };
            case resources.globalStatusItemGoodsReceived:
                return { status, date: item.ActualArrivalDate, quantity: item.DeliveredQty, unit: item.RequestedUnit };
            default:
                return { status };
        }
    };

    const renderDateRow = (
        item: IOrderDetailLine,
        resources: IOrderDetailsResources,
        localizationState: ILocalizationState
    ): React.ReactNode => {
        const latestEvent = getPreferredInfo(item, resources);
        const dateToRender = [latestEvent.date].find(d => checkIfValidDate(d));

        if (!dateToRender) {
            return (
                <>
                    {NO_DATA_FALLBACK}
                    {latestEvent.info && (
                        <span>
                            <DataTitle label={<span className='lxs-data-title__info-icon'></span>} title={latestEvent.info} />
                        </span>
                    )}
                </>
            );
        }

        const localDate = toLocalDate(dateToRender);
        const formattedDate = dateLocaleFormatter(localizationState).formatDate(localDate);

        return (
            <>
                {formattedDate}
                {latestEvent.info && (
                    <span>
                        <DataTitle label={<span className='lxs-data-title__info-icon'></span>} title={latestEvent.info} />
                    </span>
                )}
            </>
        );
    };

    const renderQtyRow = (item: IOrderDetailLine, resources: IOrderDetailsResources, localizationState: ILocalizationState): string => {
        const latestInfo = getPreferredInfo(item, resources);

        if (latestInfo.quantity && checkIfValidNumber(latestInfo.quantity)) {
            return `${formatNumberLocaleStatic(latestInfo.quantity, localizationState)} ${latestInfo.unit ?? ''}`;
        }

        return NO_DATA_FALLBACK;
    };

    const hasMultipleStatusInfo = (item: IOrderDetailLine): boolean => {
        const possibleQty = [item.RequestedQty, getFirstLine(item)?.ConfirmedQty, item.DeliveredQty];
        return possibleQty.filter(qty => qty && Number(qty) > 0).length > 1;
    };

    const hasExpandableContent = (item: IOrderDetailLine): boolean => {
        const preferred = getPreferredInfo(item, resources);
        const mainStatus = mapItemStatusCodeToText(item.StateLogistics, resources);

        return preferred.status !== mainStatus || hasMultipleStatusInfo(item);
    };

    const toggleDropdown = (index: number) => {
        setExpandedRowIndex(prev => (prev === index ? null : index));
    };

    const showEndCustomer: boolean = orderDetails.OrderDetail.Endcustomers;

    /*
     * Extracts unique delivery items from order details and stores them in state.
     * Used to display order detail content of any item in the list.
     * */
    useEffect(() => {
        const deliveryMap = new Map();

        orderDetails.OrderDetail.OrderDetailLines.forEach((line, index) => {
            if (line.DeliveryNumber !== null && line.DeliveryNumber !== '') {
                deliveryMap.set(line.DeliveryNumber, { line, index });
            }
        });
        const deliveries = Array.from(deliveryMap.values());
        setDeliveryItems(deliveries);
    }, [orderDetails]);

    useEffect(() => {
        if (modalIndex !== null) {
            setOpen({
                content: (
                    <DeliveryInformationModal
                        key={modalIndex}
                        CLASS_NAME='ms-order-details'
                        orderItemLine={deliveryItems[modalIndex].line}
                        resources={resources}
                        localizationState={localizationState}
                        deliveryItemCount={deliveryItems.length}
                        orderDetails={orderDetails.OrderDetail}
                        onPrev={goToPrev}
                        onNext={goToNext}
                    />
                ),
                header: (
                    <div className='ms-order-details__delivery-information-header-container'>
                        <div className='ms-order-details__delivery-information-header-container-heading'>
                            <span>{resources.orderDetailsModalDeliveryInformationHeading}</span>
                        </div>
                        <div className='ms-order-details__delivery-information-header-container-information'>
                            {deliveryItems.length > 1 && (
                                <button
                                    onClick={goToPrev}
                                    className='ms-order-details__delivery-information-header-container-information-previous'
                                >
                                    <span className='ms-order-details__delivery-information-header-container-information-previous-icon' />
                                </button>
                            )}
                            <span className='ms-order-details__delivery-information-header-container-information-index'>
                                {modalIndex + 1}/{deliveryItems.length}
                            </span>
                            {deliveryItems.length > 1 && (
                                <button
                                    onClick={goToNext}
                                    className='ms-order-details__delivery-information-header-container-information-next'
                                >
                                    <span className='ms-order-details__delivery-information-header-container-information-next-icon' />
                                </button>
                            )}
                        </div>
                    </div>
                ),
                size: 'xl'
            });
        }
    }, [modalIndex]);

    useEffect(() => {
        if (!isOpen && modalIndex !== null) {
            setModalIndex(null);
        }
    }, [isOpen]);

    return (
        <>
            <h3 className='ms-order-details__section-heading'>{resources.orderDetailsItemsSectionHeading}</h3>
            <div className='table-responsive'>
                <table className='table'>
                    <thead>
                        <tr>
                            <th>{resources.orderDetailsItemsTableProductName}</th>
                            <th>{resources.orderDetailsItemsTableProductNumber}</th>
                            <th>{resources.orderDetailsItemsTableCustomerProductNumber}</th>
                            <th>
                                <span>
                                    {resources.orderDetailsItemsTableDeliveryStatus}{' '}
                                    <DataTitle
                                        label={<span className='lxs-data-title__info-icon'></span>}
                                        title={formatStatusCodeForTooltip(resources, context)}
                                    />
                                </span>
                            </th>
                            <th>{resources.orderDetailsItemsTableDate}</th>
                            <th>{resources.orderDetailsItemsTableQuantity}</th>
                            <th>
                                <span>
                                    {resources.orderDetailsItemsTableInvoceStatus}{' '}
                                    <DataTitle
                                        label={<span className='lxs-data-title__info-icon'></span>}
                                        title={formatOverallStatusCodeForTooltip(resources, context)}
                                    />
                                </span>
                            </th>
                            {showEndCustomer && <th>{resources.orderDetailsItemsTableEndcustomer}</th>}
                            <th>{resources.orderDetailsItemsTableDelivery}</th>
                            <th>{resources.orderDetailsItemsTableInvoiceNumber}</th>
                            <th>
                                <span>
                                    {resources.orderDetailsItemsTableNetValue}{' '}
                                    <DataTitle
                                        label={<span className='lxs-data-title__info-icon'></span>}
                                        title={resources.globalTableNetValueToolTip}
                                    />
                                </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {orderDetails?.OrderDetail.OrderDetailLines?.map((item: IOrderDetailLine, index: number) => (
                            <React.Fragment key={index}>
                                <tr>
                                    <td>{item.ProductName}</td>
                                    <td>{item.ProductNumber}</td>
                                    <td>
                                        {item.CustomerProductNumber && Number(item.CustomerProductNumber) > 0
                                            ? item.CustomerProductNumber
                                            : NO_DATA_FALLBACK}
                                    </td>
                                    <td>
                                        {hasExpandableContent(item) ? (
                                            <div
                                                className='ms-order-details__delivery-information-status-main'
                                                onClick={() => toggleDropdown(index)}
                                            >
                                                <DataTitle
                                                    label={mapItemStatusCodeToText(item.StateLogistics, resources)}
                                                    title={mapItemStatusCodeDescriptionToText(item.StateLogistics, resources)}
                                                />
                                                <span
                                                    className={`ms-order-details__delivery-information-dropdown-button-icon ${
                                                        expandedRowIndex === index ? 'dropdown-open' : ''
                                                    }`}
                                                ></span>
                                            </div>
                                        ) : (
                                            <DataTitle
                                                label={mapItemStatusCodeToText(item.StateLogistics, resources)}
                                                title={mapItemStatusCodeDescriptionToText(item.StateLogistics, resources)}
                                            />
                                        )}
                                    </td>
                                    <td>
                                        <div className='ms-order-details__delivery-information-date-main'>
                                            {renderDateRow(item, resources, localizationState)}
                                        </div>
                                    </td>
                                    <td>
                                        <div className='ms-order-details__delivery-information-qty-main'>
                                            {renderQtyRow(item, resources, localizationState)}
                                        </div>
                                    </td>
                                    <td>
                                        <div>
                                            <DataTitle
                                                label={mapItemInvoiceStatusCodeToText(item.StateInvoice, resources)}
                                                title={mapItemStatusCodeDescriptionToText(item.StateLogistics, resources)}
                                            />
                                        </div>
                                    </td>
                                    {showEndCustomer && <td>{item.Endcustomer}</td>}
                                    <td>
                                        {item.DeliveryNumber && item.Legs && item.Legs.length >= 1 ? (
                                            <a
                                                onClick={() => {
                                                    const deliveryIndex = deliveryItems.findIndex(
                                                        d => d.line.DeliveryNumber === item.DeliveryNumber
                                                    );
                                                    setModalIndex(deliveryIndex);
                                                }}
                                                title={resources.orderDetailsShowDeliveryInformationTitle}
                                                className='ms-order-details__block-item ms-order-details__link-pointer ms-order-details__delivery-number'
                                            >
                                                {item.DeliveryNumber}
                                                {!item.TrackingAvailable ||
                                                (item.Legs &&
                                                    item.Legs[item.Legs.length - 1].State ===
                                                        DELIVERY_INFORMATION_LEG_STATES.Completed) ? null : (
                                                    <span className='ms-order-details__delivery-number-tracking ms-order-details__delivery-number-tracking-delivery-live'>
                                                        <span className='ms-order-details__delivery-number-tracking-indicator' />
                                                        {resources.orderDetailsTrackingAvailable}
                                                    </span>
                                                )}
                                            </a>
                                        ) : (
                                            <span className='ms-order-details__block-item'>-</span>
                                        )}
                                    </td>
                                    <td>
                                        <span className='ms-order-details__block-item'>
                                            {item.InvoiceNumber.length !== 0 ? item.InvoiceNumber : NO_DATA_FALLBACK}
                                        </span>
                                    </td>
                                    <td className='ms-order-details__block-item'>
                                        {item.Currency
                                            ? formatCurrency(localizationState, item.Currency, Number(item.NetValue))
                                            : NO_DATA_FALLBACK}
                                    </td>
                                </tr>
                                <ExpandedRows
                                    item={item}
                                    resources={resources}
                                    localizationState={localizationState}
                                    isExpanded={expandedRowIndex === index}
                                    CLASS_NAME='ms-order-details__delivery-information-expanded'
                                />
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
            {orderDetails.OrderDetail.ReorderAllowed && (
                <a
                    className='msc-cta__primary ms-order-details__reorder-button'
                    onClick={() =>
                        setOpen({
                            content: (
                                <div>
                                    <p className='mb-2'>{resources.orderDetailsReorderConfirmationHint}</p>
                                    <a
                                        className='msc-cta__primary'
                                        href={getReorderPageUrl(context.actionContext, orderDetails.OrderDetail.OrderNumber ?? '')}
                                    >
                                        {resources.orderDetailsReorderConfirmation}
                                    </a>
                                </div>
                            ),
                            size: 'sm'
                        })
                    }
                >
                    {resources.orderDetailsReorderLabel}
                </a>
            )}
        </>
    );
}
